import {
    CANCELLATION_COMMENT_MAX_LENGTH,
    type PartnerCockpitSubscription,
} from '@houzy/schemas/partner-cockpit/subscription';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useState, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { houzyContact } from '../../../lib/houzyContact';
import { formatSwissDate } from '../../../lib/time';
import { subscriptionCancelBody } from '../labels';

export type CancelSubscriptionDialogProps = {
    subscription: PartnerCockpitSubscription;
    open: boolean;
    isSubmitting: boolean;
    errorMessage?: string;
    onConfirm: (comment: string) => void;
    onCancel: () => void;
};

export function CancelSubscriptionDialog({
    subscription,
    open,
    isSubmitting,
    errorMessage,
    onConfirm,
    onCancel,
}: CancelSubscriptionDialogProps): ReactElement {
    const { t } = useTranslation();
    const [comment, setComment] = useState('');

    const effectiveAt = subscription.cancelWouldEndAt;

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth aria-labelledby="cancel-subscription-title">
            <DialogTitle id="cancel-subscription-title">{t('SETTINGS.SUBSCRIPTION.CANCEL.TITLE')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <DialogContentText>
                        {subscriptionCancelBody(
                            t,
                            subscription.endsCoveredPeriod,
                            effectiveAt ? formatSwissDate(new Date(effectiveAt)) : undefined,
                        )}
                    </DialogContentText>

                    <DialogContentText variant="body2">
                        <Trans
                            i18nKey="SETTINGS.SUBSCRIPTION.CANCEL.CONTACT_SUPPORT"
                            components={{
                                supportLink: <Link href={`mailto:${houzyContact.supportEmail}`} underline="hover" />,
                            }}
                        />
                    </DialogContentText>

                    <TextField
                        label={t('SETTINGS.SUBSCRIPTION.CANCEL.REASON')}
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        slotProps={{ htmlInput: { maxLength: CANCELLATION_COMMENT_MAX_LENGTH } }}
                    />

                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>{t('SETTINGS.SUBSCRIPTION.CANCEL.KEEP')}</Button>
                <Button variant="contained" color="error" disabled={isSubmitting} onClick={() => onConfirm(comment)}>
                    {t('SETTINGS.SUBSCRIPTION.CANCEL.CONFIRM')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
